import { send } from '@actual-app/core/platform/client/connection';
/**
 * Rumbo: borrar la cuenta propia desde Ajustes (lo exigen Google Play y el
 * RGPD). Pide el borrado al servidor (POST /rumbo/cuenta/borrar, ver
 * sync-server/src/app-rumbo-borrado.ts) y, si sale bien, limpia lo guardado
 * en este dispositivo y vuelve a la pantalla de entrada.
 */
import type { TFunction } from 'i18next';

import { setUsoActivado } from '#uso';

/** Lo mismo que espera el servidor en `confirmacion`. */
export const CONFIRMACION_BORRAR = 'BORRAR';

export type MotivoFallo =
  | 'presupuestos-compartidos'
  | 'ultimo-admin'
  | 'propietario-del-servidor'
  | 'sin-servidor'
  | 'sesion'
  | 'red'
  | 'error';

export type FalloBorrado = {
  ok: false;
  motivo: MotivoFallo;
  presupuestos?: string[];
};

export type ResultadoBorrado = { ok: true } | FalloBorrado;

export function esFallo(
  resultado: ResultadoBorrado,
): resultado is FalloBorrado {
  return 'motivo' in resultado;
}

const RECHAZOS: MotivoFallo[] = [
  'presupuestos-compartidos',
  'ultimo-admin',
  'propietario-del-servidor',
];

/** Traduce la respuesta del servidor a un resultado. */
export function interpretarRespuesta(
  estado: number,
  cuerpo: unknown,
): ResultadoBorrado {
  if (estado >= 200 && estado < 300) {
    return { ok: true };
  }
  if (estado === 401 || estado === 404) {
    return { ok: false, motivo: 'sesion' };
  }
  const datos =
    typeof cuerpo === 'object' && cuerpo !== null
      ? (cuerpo as { reason?: unknown; details?: unknown })
      : {};
  const motivo = RECHAZOS.find(r => r === datos.reason);
  if (estado === 409 && motivo) {
    const detalles = datos.details as { presupuestos?: unknown } | undefined;
    const presupuestos = Array.isArray(detalles?.presupuestos)
      ? detalles.presupuestos.filter((p): p is string => typeof p === 'string')
      : undefined;
    return { ok: false, motivo, presupuestos };
  }
  return { ok: false, motivo: 'error' };
}

/** Pide al servidor que borre la cuenta de quien tiene la sesión abierta. */
export async function pedirBorradoCuenta(): Promise<ResultadoBorrado> {
  let url: string | null = null;
  let token: string | null = null;
  try {
    ({ url, token } = await send('rumbo-uso-conexion'));
  } catch {
    return { ok: false, motivo: 'sin-servidor' };
  }
  if (!url || !token) {
    return { ok: false, motivo: 'sin-servidor' };
  }
  try {
    const respuesta = await fetch(`${url}/rumbo/cuenta/borrar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-ACTUAL-TOKEN': token,
      },
      body: JSON.stringify({ confirmacion: CONFIRMACION_BORRAR }),
    });
    const cuerpo: unknown = await respuesta.json().catch(() => null);
    return interpretarRespuesta(respuesta.status, cuerpo);
  } catch {
    return { ok: false, motivo: 'red' };
  }
}

/** Mensaje para la persona cuando el borrado no se hace. */
export function mensajeFallo(t: TFunction, resultado: FalloBorrado): string {
  switch (resultado.motivo) {
    case 'presupuestos-compartidos':
      return t(
        'Other people can edit {{budgets}}. Transfer it or stop sharing it first, then try again.',
        { budgets: (resultado.presupuestos ?? []).join(', ') },
      );
    case 'ultimo-admin':
      return t(
        'You are the only administrator of this server. Make another person administrator first.',
      );
    case 'propietario-del-servidor':
      return t(
        'This account owns the server, so it cannot be deleted from the app.',
      );
    case 'sesion':
      return t('Your session has ended. Sign in again and try once more.');
    case 'sin-servidor':
    case 'red':
      return t('Could not reach the server. Try again later.');
    default:
      return t('Something went wrong. Nothing has been deleted.');
  }
}

/** Borra las bases de IndexedDB (sin esperar más de `esperaMs` a cada una). */
async function borrarBasesIndexedDb(esperaMs: number) {
  if (typeof indexedDB === 'undefined' || !indexedDB.databases) {
    return;
  }
  let bases: IDBDatabaseInfo[] = [];
  try {
    bases = await indexedDB.databases();
  } catch {
    return;
  }
  await Promise.all(
    bases.map(
      ({ name }) =>
        new Promise<void>(resolve => {
          if (!name) {
            resolve();
            return;
          }
          const fin = setTimeout(resolve, esperaMs);
          const listo = () => {
            clearTimeout(fin);
            resolve();
          };
          try {
            const peticion = indexedDB.deleteDatabase(name);
            peticion.onsuccess = listo;
            peticion.onerror = listo;
            // Bloqueada (el motor la tiene abierta): se borra al cerrarse.
            peticion.onblocked = listo;
          } catch {
            listo();
          }
        }),
    ),
  );
}

/**
 * Tras borrar la cuenta: cierra el presupuesto, borra las copias locales de
 * todos los presupuestos, la sesión y lo guardado en el navegador
 * (localStorage, sessionStorage e IndexedDB). Cada paso sigue aunque falle
 * el anterior.
 */
export async function borrarDatosLocales({ esperaMs = 2000 } = {}) {
  setUsoActivado(false);
  try {
    await send('close-budget');
  } catch {
    // sin presupuesto abierto
  }
  try {
    const presupuestos = await send('get-budgets');
    for (const presupuesto of presupuestos) {
      if (presupuesto.id) {
        await send('delete-budget', { id: presupuesto.id });
      }
    }
  } catch {
    // se intenta igual con IndexedDB
  }
  try {
    await send('save-global-prefs', { nombrePerfil: '' });
    await send('subscribe-sign-out');
  } catch {
    // se intenta igual con IndexedDB
  }
  try {
    window.localStorage.clear();
    window.sessionStorage.clear();
  } catch {
    // sin almacenamiento
  }
  await borrarBasesIndexedDb(esperaMs);
}
